import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Eye, EyeOff } from 'lucide-react';
import { PROFILE_CARD } from '@dove/shared';
import { Field } from '@/components/Field';
import { Pigeon } from '@/components/Pigeon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  ApiError,
  getProviders,
  signInWithPassword,
  signUpWithPassword,
  startSocialSignIn,
} from '@/lib/api';
import { recallInvite } from '@/lib/invite';
import { keys } from '@/lib/query-client';
import { useSession } from '@/lib/session';

/**
 * The way in. Everything in this product is behind it (docs/2 § Indexability: user
 * content is never public, so there is no logged-out surface to land on).
 *
 * WHAT IS ON IT: the mark, the ways in, and nothing else.
 *
 * No tagline, no screenshots, no three-bullet pitch, no "welcome to Dove!", no
 * paragraph about how friendship works here. Somebody at this screen either has an
 * account or is making one; both of those are two fields and a button, and every
 * sentence added to the page is one more thing between a person and the app. docs/1
 * § Copy in the interface is the rule and this is the screen it is easiest to break
 * it on, because a sign-in page is where products traditionally advertise themselves.
 *
 * THE PROVIDER BUTTONS ARE WHAT THE SERVER SAYS THEY ARE. `GET /auth/providers`
 * lists what is configured, so an unconfigured Google is a button that is not there
 * rather than one that fails when pressed. Today that list is empty and the password
 * form is the only way in, which is exactly why the form is the primary thing on the
 * screen rather than a "more options" afterthought.
 *
 * THE FORM IS AT THE BOTTOM. The mark floats in the space above it, so the fields and
 * the button are in the half of the screen a thumb reaches — the same rule the tab
 * bar and every other control in this app follows (CLAUDE.md), and it means the
 * keyboard rising does not push the submit button off screen.
 *
 * ONE FORM, TWO MODES, and no second screen. Sign in and create account differ by one
 * field and one endpoint; two routes would double the layout and give somebody who
 * mistyped their password a navigation to perform. The mode is local state: it is not
 * a place, and it survives nothing worth surviving.
 *
 * A RULE THE BUTTON KNOWS IS A RULE THE BUTTON MUST SAY (docs/5 § D260808-2005). The
 * length minimum used to hold the button dead, so a short password produced a press
 * that did nothing and no sentence anywhere — the commonest way this screen was
 * abandoned. The button is live as soon as both fields have something in them, and a
 * password the server would refuse is refused HERE, in words, under the field.
 *
 * ERRORS ARE THE SERVER'S OWN SENTENCE (`ApiError.message`), rendered under the
 * fields, and nothing is guessed on top of them. Better Auth answers a wrong password
 * and an unknown email the same way, which is deliberate on its side and must not be
 * decorated on this one — a sign-in page that distinguishes them is an account
 * oracle.
 */

const PROVIDER_LABEL: Record<string, string> = {
  google: 'Continue with Google',
  apple: 'Continue with Apple',
};

type Mode = 'in' | 'up';

/**
 * Better Auth exposes a few transport-level failures that are accurate for a
 * developer but not actionable for someone trying to sign in. Keep credential
 * and rate-limit messages intact; only translate the request-format failure.
 */
function signInErrorMessage(
  error: unknown,
  fallback = 'That didn’t work. Try again.',
): string {
  if (!(error instanceof ApiError)) return fallback;
  if (error.code === 'UNSUPPORTED_MEDIA_TYPE') {
    return 'We couldn’t send that sign-in request. Please try again.';
  }
  return error.message;
}

export function SignInScreen() {
  const { refresh } = useSession();
  /*
   * SOMEBODY HOLDING AN INVITATION IS NEW, so this opens on *Create account* for
   * them and on *Sign in* for everybody else (docs/5 § D260805-0328).
   *
   * IT IS READ FROM THE DEVICE RATHER THAN PASSED IN, and that is what lets
   * `JoinScreen` carry one button instead of two. An unspent `/join/…` code is
   * only ever on the device of somebody who has not finished an account —
   * `OnboardingScreen` clears it the moment one exists — so its presence answers
   * "which mode" without a URL parameter, a route search schema, or a prop
   * threaded through the gate.
   *
   * `useState`'s initialiser, so it is read ONCE. Recomputing it per render would
   * fight somebody who pressed the toggle below.
   */
  const [mode, setMode] = useState<Mode>(() => (recallInvite() ? 'up' : 'in'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(
    /*
     * A failed social sign-in returns here with `?error=<code>` — that is what
     * `errorCallbackURL` buys. Read once, on mount, so the reason is on screen
     * instead of the person pressing the same button again.
     *
     * The codes are the provider's and the library's rather than ours, so they are
     * mapped to a sentence where the cause is actionable and shown plainly where it
     * is not. `access_denied` is the ordinary case — somebody changed their mind at
     * the consent screen — and it is not an error worth shouting about.
     */
    () => {
      const code = new URLSearchParams(window.location.search).get('error');
      if (!code) return null;
      window.history.replaceState({}, '', window.location.pathname);
      if (code === 'access_denied') return null;
      const known: Record<string, string> = {
        account_not_linked:
          'That email already has an account. Sign in the way you did before.',
        unable_to_create_user: 'That account could not be created.',
        invalid_state: 'That sign-in expired. Try again.',
      };
      return known[code] ?? 'That sign-in did not complete.';
    },
  );
  /** Which provider is mid-handshake, so its button cannot be pressed twice. */
  const [pendingProvider, setPendingProvider] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** Whether the password is on screen. See the toggle below. */
  const [revealed, setRevealed] = useState(false);
  /**
   * Whether the length rule has been BROKEN in front of somebody yet — set when
   * they try to submit, and when they leave the field with something too short in
   * it. Until then the field says nothing: eleven characters on the way to twelve
   * is a password being typed, not a mistake, and a red line that lands on the
   * first keystroke is the form telling somebody off for starting (`cardProblems`
   * holds the same line).
   */
  const [ruleBroken, setRuleBroken] = useState(false);
  const passwordField = useRef<HTMLInputElement>(null);

  /* Not gated on: an empty list is the ordinary case today, and a failed lookup
     should cost the password form nothing. */
  const providers = useQuery({
    queryKey: keys.providers,
    queryFn: getProviders,
    staleTime: Infinity,
  });

  const creating = mode === 'up';
  const tooShort =
    creating &&
    password.length > 0 &&
    password.length < PROFILE_CARD.MIN_PASSWORD_CHARS;
  /*
   * THE RULE, RESTATED AS THE REFUSAL, rather than "Too short." The hint beside
   * the label is muted and already read past by the time somebody is here, and a
   * `role="alert"` is announced on its own — so the sentence that arrives has to
   * carry the number. One number, from `MIN_PASSWORD_CHARS`, in both places.
   */
  const passwordProblem =
    tooShort && ruleBroken
      ? `At least ${PROFILE_CARD.MIN_PASSWORD_CHARS} characters.`
      : null;

  /*
   * PRESENCE ONLY. An empty field is "not yet" and is expressed by a button that is
   * not live; anything WRONG with what was typed is expressed in words instead
   * (docs/5 § D260808-2005). A dead button is the one control that cannot explain
   * itself, so it may never be the thing standing between somebody and a rule they
   * cannot see.
   */
  const canSubmit =
    Boolean(email.trim()) &&
    Boolean(password) &&
    (!creating || Boolean(name.trim())) &&
    !busy;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    if (tooShort) {
      /* Said, then focused — the field with the problem is the field the caret and
         the keyboard should be in, and on a phone it may be off screen behind the
         keyboard by now. */
      setRuleBroken(true);
      passwordField.current?.focus();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (creating) {
        await signUpWithPassword(email.trim(), password, name.trim());
      } else {
        await signInWithPassword(email.trim(), password);
      }
      /* The session cookie is set by now, so re-reading `/me` is what moves the app
         off this screen — either into onboarding or into the feed. The gate decides
         which; this screen does not navigate. */
      await refresh();
    } catch (err) {
      setError(signInErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto flex h-full w-full max-w-sm flex-col px-6 pt-14 pb-8">
      {/* The mark, large, in the space above the form. `Pigeon` and the wordmark at
          the size a first screen can afford — this is the one place in the product
          where the name is worth more than a 17px label. */}
      <div className="flex flex-1 flex-col items-center justify-center gap-3">
        <Pigeon className="w-16" />
        <h1 className="text-[30px] leading-none font-semibold tracking-tight">
          Dove
        </h1>
      </div>

      {/* `noValidate` because the refusals are this screen's, not the browser's. The
          constraint attributes below stay — they are what a password manager and a
          screen reader read — but a native bubble in the browser's own voice, in the
          browser's own language, placed wherever the browser likes, is not a thing
          this product says. It also swallows the submit event, which is how the
          length rule would go back to being unsayable. */}
      <form onSubmit={submit} noValidate className="flex flex-col gap-3">
        {creating && (
          <Field label="Name">
            {(props) => (
              <Input
                {...props}
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                autoCapitalize="words"
                enterKeyHint="next"
                maxLength={PROFILE_CARD.DISPLAY_NAME_MAX_CHARS}
                required
              />
            )}
          </Field>
        )}

        <Field label="Email">
          {(props) => (
            <Input
              {...props}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              inputMode="email"
              enterKeyHint="next"
              required
            />
          )}
        </Field>

        <Field
          label="Password"
          /*
           * THE MINIMUM IS ON THE LABEL, AND ONLY WHILE MAKING AN ACCOUNT.
           *
           * It is a constraint rather than an explanation: the rule that decides
           * whether the button works, said where the choice is made. A signup form
           * that keeps the length rule secret until the submit fails is the ambush
           * this replaces — and it is the commonest way a signup fails.
           *
           * It is NOT shown when signing in, where the minimum is nobody's business:
           * the password already exists, and rendering the current rule beside it
           * would tell somebody with an older, shorter password that theirs is
           * wrong when it still works perfectly.
           *
           * The number comes from `MIN_PASSWORD_CHARS`, which the server reads for
           * the same rule — one number, two tiers (CLAUDE.md).
           */
          hint={
            creating ? `${PROFILE_CARD.MIN_PASSWORD_CHARS} characters` : undefined
          }
          error={passwordProblem}
        >
          {(props) => (
            <div className="relative">
              <Input
                {...props}
                ref={passwordField}
                type={revealed ? 'text' : 'password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  /* Emptying the field is starting over, so the complaint goes with
                     it — otherwise a red line is waiting on the first keystroke of
                     the second attempt. */
                  if (!e.target.value) setRuleBroken(false);
                }}
                /* Leaving a half-length password behind is the other way somebody
                   finds out too late. They are done with the field; say it now
                   rather than at the button. */
                onBlur={() => {
                  if (tooShort) setRuleBroken(true);
                }}
                /* The browser is told which of the two this is, or it offers to save a
                   new password over an existing one. */
                autoComplete={creating ? 'new-password' : 'current-password'}
                enterKeyHint="go"
                minLength={creating ? PROFILE_CARD.MIN_PASSWORD_CHARS : undefined}
                className="pe-11"
                required
              />
              {/*
                SHOW THE PASSWORD, because on a phone the alternative is typing
                twelve characters of something you have just invented, blind, on a
                keyboard that autocorrects — and the failure mode is a typo in the
                credential that locks somebody out of an account they have not
                finished making. It is a toggle rather than a press-and-hold: a
                gesture nobody can perform one-handed is not an affordance.

                `aria-pressed` rather than two labels, so the state is the button's
                rather than something a screen reader has to infer from an icon.
              */}
              <button
                type="button"
                onClick={() => setRevealed((on) => !on)}
                aria-pressed={revealed}
                aria-label="Show password"
                className="absolute end-1 top-1/2 grid size-9 -translate-y-1/2 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-muted focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none"
              >
                {revealed ? (
                  <EyeOff aria-hidden className="size-4" />
                ) : (
                  <Eye aria-hidden className="size-4" />
                )}
              </button>
            </div>
          )}
        </Field>

        {error && (
          /* Under the fields, in the flow, so the button does not move when it
             appears and disappears. */
          <p role="alert" className="px-1 text-[13px] text-destructive">
            {error}
          </p>
        )}

        <Button
          type="submit"
          size="lg"
          className="mt-1 h-11 rounded-full text-[15px]"
          disabled={!canSubmit}
        >
          {creating ? 'Create account' : 'Sign in'}
        </Button>
      </form>

      {providers.data?.providers.length ? (
        <div className="mt-2 flex flex-col gap-2">
          {providers.data.providers.map((provider) => (
            <Button
              key={provider}
              variant="outline"
              size="lg"
              className="h-11 rounded-full text-[15px]"
              /* A POST, THEN A NAVIGATION. Better Auth's social endpoint accepts
                 POST only and answers with the URL to go to — navigating straight
                 at it is a GET and 404s, which is what this button did at first.
                 The POST is also what mints the `state` and PKCE verifier, so
                 there is nothing to skip here.

                 `errorCallbackURL` brings a refusal back to this screen instead of
                 the library's bare error page; the notice below reads it. */
              disabled={pendingProvider !== null}
              onClick={() => {
                setPendingProvider(provider);
                setError(null);
                startSocialSignIn(
                  provider,
                  window.location.origin,
                  window.location.origin,
                )
                  .then((url) => {
                    window.location.href = url;
                  })
                  .catch((err: unknown) => {
                    setPendingProvider(null);
                    setError(
                      signInErrorMessage(
                        err,
                        "That sign-in method isn't available.",
                      ),
                    );
                  });
              }}
            >
              {PROVIDER_LABEL[provider] ??
                `Continue with ${provider[0]?.toUpperCase()}${provider.slice(1)}`}
            </Button>
          ))}
        </div>
      ) : null}

      {/* The other mode. A link rather than a second button, because it changes what
          this screen is rather than doing anything. */}
      <Button
        variant="ghost"
        className="mt-3 h-10 self-center rounded-full px-4 text-[13.5px] text-muted-foreground"
        onClick={() => {
          setMode(creating ? 'in' : 'up');
          setError(null);
          setRuleBroken(false);
        }}
      >
        {creating ? 'I already have an account' : 'Create an account'}
      </Button>
    </div>
  );
}
